import { useState } from 'react';
import { useGT } from 'gt-react';
import { ColumnsIcon, SlidersHorizontalIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { parseOverrideDisplay } from '../utils/overrideDisplay';
import { BuyerBoardColumnsDialog } from './BuyerBoardColumnsDialog';
import type { BuyerBoardColumnConfig } from './useBuyerBoardColumnConfig';
import type { HiddenColumns } from './useColumnsHiddenByFilters';
import type { OverrideDisplayPref } from './useOverrideDisplay';

/** A board table's independent view settings. */
export interface TableView {
  /** The saved column setup (with the ordered sort on the line table). */
  config?: BuyerBoardColumnConfig;
  /** Only the line table has quantity overrides (saved with its setup). */
  overrideDisplay?: OverrideDisplayPref;
  columns: HiddenColumns;
}

/**
 * A board table's view menu: the column and sort setup and how a quantity
 * override shows (line table), and the columns a filter narrowed to one value,
 * hidden until the buyer shows them again.
 */
export function TableViewMenu({
  view,
  columnLabels,
  className,
}: {
  view: TableView;
  /** Header text per filterable column id. */
  columnLabels: Record<string, string>;
  className?: string;
}) {
  const { config, overrideDisplay, columns } = view;
  const gt = useGT();
  const [setupOpen, setSetupOpen] = useState(false);
  const hiddenCount = Object.values(columns.columnVisibility).filter(
    (visible) => !visible,
  ).length;
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="ghost"
            className={cn('h-7 gap-1 px-2 text-xs', className)}
          >
            <SlidersHorizontalIcon aria-hidden className="h-4 w-4" />
            {gt('View', { context: 'buyer board line table view menu button' })}
            {hiddenCount > 0 && (
              <span className="text-muted-foreground">
                {gt('· {count} hidden', {
                  context:
                    'buyer board line table view menu, columns hidden by a filter',
                  count: hiddenCount,
                })}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          {config && (
            <DropdownMenuItem
              className="text-xs"
              onSelect={() => setSetupOpen(true)}
            >
              <ColumnsIcon aria-hidden className="h-4 w-4" />
              {config.table === 'lines'
                ? gt('Columns and sort…', {
                    context:
                      'buyer board line table view menu item: opens the column and sort setup',
                  })
                : gt('Columns…', {
                    context:
                      'buyer board orders table view menu item: opens the column setup',
                  })}
            </DropdownMenuItem>
          )}
          {config && overrideDisplay && <DropdownMenuSeparator />}
          {overrideDisplay && <OverrideDisplayItems pref={overrideDisplay} />}
          {(config || overrideDisplay) &&
            columns.hiddenByFilters.length > 0 && <DropdownMenuSeparator />}
          <HiddenColumnItems columns={columns} columnLabels={columnLabels} />
        </DropdownMenuContent>
      </DropdownMenu>
      {config && (
        <BuyerBoardColumnsDialog
          open={setupOpen}
          onOpenChange={setSetupOpen}
          config={config}
        />
      )}
    </>
  );
}

function OverrideDisplayItems({ pref }: { pref: OverrideDisplayPref }) {
  const gt = useGT();
  return (
    <>
      <DropdownMenuLabel className="text-xs">
        {gt('Quantity overrides', {
          context: 'buyer board line table view menu section',
        })}
      </DropdownMenuLabel>
      <DropdownMenuRadioGroup
        value={pref.overrideDisplay}
        onValueChange={(value) =>
          pref.setOverrideDisplay(parseOverrideDisplay(value))
        }
      >
        <DropdownMenuRadioItem value="separate" className="text-xs">
          {gt('Separate', { context: 'buyer board override display option' })}
          <span className="ml-auto text-muted-foreground">
            {gt('Calc beside Qty', {
              context: 'buyer board override display option, separate hint',
            })}
          </span>
        </DropdownMenuRadioItem>
        <DropdownMenuRadioItem value="simple" className="text-xs">
          {gt('Simple', { context: 'buyer board override display option' })}
          <span className="ml-auto text-muted-foreground">
            {gt('Calc on hover', {
              context: 'buyer board override display option, simple hint',
            })}
          </span>
        </DropdownMenuRadioItem>
      </DropdownMenuRadioGroup>
    </>
  );
}

function HiddenColumnItems({
  columns,
  columnLabels,
}: {
  columns: HiddenColumns;
  columnLabels: Record<string, string>;
}) {
  const gt = useGT();
  if (columns.hiddenByFilters.length === 0) return null;
  return (
    <>
      <DropdownMenuLabel className="text-xs">
        {gt('Filtered to one value', {
          context:
            'buyer board line table view menu section, columns a filter narrowed to a single value',
        })}
      </DropdownMenuLabel>
      {columns.hiddenByFilters.map((column) => (
        <DropdownMenuCheckboxItem
          key={column.id}
          checked={columns.shownColumnIds.has(column.id)}
          onCheckedChange={() => columns.toggleShown(column.id)}
          // Keep the menu open so several columns can come back at once.
          onSelect={(event) => event.preventDefault()}
          className="text-xs"
        >
          {columnLabels[column.id] ?? column.id}
          <span className="ml-auto truncate pl-2 text-muted-foreground">
            {column.value || '—'}
          </span>
        </DropdownMenuCheckboxItem>
      ))}
    </>
  );
}
